import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Btn, FieldRow, Section, inputCls } from "@/components/account/Shell";
import { Cell, IconBtn, Sheet, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";
import { when } from "@/lib/when";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const CLASS_COLS = [
    { key: "title", label: "Class", w: "minmax(220px,1.6fr)" },
    { key: "date", label: "Date", w: "120px" },
    { key: "time", label: "Time", w: "110px" },
    { key: "price", label: "Price", sub: "$ per person", w: "90px" },
    { key: "seats", label: "Seats", w: "80px" },
    { key: "actions", label: "Actions", sub: "delete", w: "80px" },
];
const REG_COLS = [
    { key: "when", label: "Signed up", sub: "date · how long ago", w: "150px" },
    { key: "name", label: "Who signed up", w: "minmax(180px,1fr)" },
    { key: "email", label: "Email", w: "minmax(220px,1.2fr)" },
    { key: "class", label: "Class", w: "minmax(220px,1.4fr)" },
];
const BLANK_CLASS = { title: "", description: "", date: "", time: "", price: "", seats: "", image: "https://13thav.com/shirts/classes.jpg" };

/** Vaulted (flag `classes`): the admin Classes tab — board, registrations, add-a-class form. */
export default function ClassesAdmin() {
    const [classes, setClasses] = useState([]);
    const [regs, setRegs] = useState([]);
    const [newClass, setNewClass] = useState(BLANK_CLASS);
    const load = () => {
        api.get("/classes").then(({ data }) => setClasses(data.classes)).catch(() => {});
        api.get("/admin/class-registrations").then(({ data }) => setRegs(data.registrations)).catch(() => {});
    };
    useEffect(load, []);
    const createClass = async () => {
        try { await api.post("/admin/classes", { ...newClass, price: +newClass.price, seats: +newClass.seats }); toast.success("Class added"); setNewClass(BLANK_CLASS); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const delClass = async (id) => { await api.delete(`/admin/classes/${id}`); load(); };
    const setC = (k) => (e) => setNewClass({ ...newClass, [k]: e.target.value });

    return (
        <div className="space-y-3" data-testid="admin-classes">
            <Section title={`${classes.length} classes`} pad={false} testid="admin-classes-sheet" note="what's on the public classes board">
                <Sheet cols={CLASS_COLS} rows={classes} rowKey={(c) => c.id} testid="classes-sheet" minWidth={760}
                    empty="No classes on the board." rowTestid={(c) => `admin-class-${c.id}`}
                    cells={(c) => ({
                        title: <Cell><Txt bold>{c.title}</Txt></Cell>,
                        date: <Cell><Txt>{c.date}</Txt></Cell>,
                        time: <Cell><Txt>{c.time}</Txt></Cell>,
                        price: <Cell><Txt mono right>{money(c.price)}</Txt></Cell>,
                        seats: <Cell><Txt mono right>{c.seats}</Txt></Cell>,
                        actions: <Cell className="justify-end"><IconBtn onClick={() => delClass(c.id)} title="Delete this class" danger testid={`admin-class-delete-${c.id}`}><Trash2 size={12} /></IconBtn></Cell>,
                    })} />
            </Section>
            <Section title={`${regs.length} registrations`} pad={false} testid="admin-registrations" note="seats booked from the classes page">
                <Sheet cols={REG_COLS} rows={regs} rowKey={(r) => r.id} testid="registrations-sheet" minWidth={700}
                    empty="Nobody has signed up yet."
                    cells={(r) => ({
                        when: <Cell><Txt sub>{when(r.created_at)}</Txt></Cell>,
                        name: <Cell><Txt bold>{r.name}</Txt></Cell>,
                        email: <Cell><Txt>{r.email}</Txt></Cell>,
                        class: <Cell><Txt>{r.class_title}</Txt></Cell>,
                    })} />
            </Section>
            <Section title="Add a class" pad={false} testid="admin-class-form" note="one field per line · lands on the board the moment you add it">
                <FieldRow label="Title" hint="what the class is called on the board">
                    <input className={inputCls} placeholder="Custom Garment Printing Workshop" value={newClass.title} onChange={setC("title")} data-testid="class-title-input" />
                </FieldRow>
                <FieldRow label="Description" hint="what they'll learn, in a sentence or two" top>
                    <textarea className={inputCls} rows={2} placeholder="Two hours on the press: art prep, heat, pressure, time." value={newClass.description} onChange={setC("description")} data-testid="class-description-input" />
                </FieldRow>
                <FieldRow label="Date">
                    <input className={`${inputCls} max-w-[220px]`} type="date" value={newClass.date} onChange={setC("date")} data-testid="class-date-input" />
                </FieldRow>
                <FieldRow label="Time" hint="as you'd say it — 1:00 PM">
                    <input className={`${inputCls} max-w-[220px]`} placeholder="1:00 PM" value={newClass.time} onChange={setC("time")} data-testid="class-time-input" />
                </FieldRow>
                <FieldRow label="Price per person" hint="$ per seat">
                    <input className={`${inputCls} max-w-[160px]`} type="number" min="0" placeholder="99" value={newClass.price} onChange={setC("price")} data-testid="class-price-input" />
                </FieldRow>
                <FieldRow label="Seats" hint="how many people fit">
                    <input className={`${inputCls} max-w-[160px]`} type="number" min="1" placeholder="3" value={newClass.seats} onChange={setC("seats")} data-testid="class-seats-input" />
                </FieldRow>
                <div className="border-t border-[#e5e5e5] px-2.5 py-2">
                    <Btn tone="solid" onClick={createClass} testid="add-class-button"><Plus size={11} /> Add class</Btn>
                </div>
            </Section>
        </div>
    );
}
